import React, { useEffect, useState, useRef } from 'react'
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom'

const WebLoginPage = React.lazy(() => import('@pages/WebLoginPage'))
import { useSelector } from 'react-redux'
import { getEffectiveStoreId, getSelectedStoreId } from '@atredux/selectors/storeSelector'
import { NO_CURRENT_STORE } from '@src/constants'
import { store } from '@atredux/store'
import { EnvironmentDetail, MetricsBatcher, MetricsRecorder, ApplicationTimeline, DefaultTelemetryConfig, PageLatencyTracker } from '@amzn/ihm-associate-tools-js-lib'
import { CrossFrameUtils, isReactNativeApp, useBackButtonNavigation, MonitoringInterceptor } from '@amzn/jwo-store-management-platform-components'
import { Telemetry } from '@amzn/ihm-telemetry-js-lib'
import { registerCrossFrameListeners } from '@crossframe/CrossFrameListenerService'
import ToolLayout from '@components/ToolLayout'
import ProvisionPage from '@pages/Provision'
import LogoutPage from '@pages/LogoutPage'
import StoreIdSelectionPage from '@pages/StoreIdSelection'
import UserTypeSelectionPage from '@pages/UserTypeSelection'
import SeawardIFrameController from '@components/SeawardIFrameController'
import IFrameController from '@src/components/IFrameController'
import * as FeatureConfigService from '@services/FeatureConfigService'
import '@utils/SXClient'
import * as ActivityHeartbeatService from '@services/ActivityHeartbeatService'
import StoreIdSelectionPageFor3P from '@src/pages/StoreIdSelectionFor3P'
import StoreIdInputPage from '@src/pages/StoreIdInput'
import PlatformModeSelector from '@components/PlatformModeSelector'
import StoreSelectionSheetController from '@src/pages/StoreSelectionPage/StoreSelectionSheetController'
import * as ZoneService from '@src/services/ZoneModule'
import * as AcknowledgeNotificationService from '@src/services/notifications/AcknowledgeNotificationService'
import * as PushNotificationService from '@src/services/notifications/PushNotificationService'
import * as HeliosInterruptService from '@src/services/HeliosInterruptService'
import * as StoreService from '@src/services/StoreService'
import { initNetworkMonitoring } from '@src/services/NetworkMonitoringService'
import { NetworkBanner } from '@src/components/network/NetworkBanner'
import { getCordovaReadyPromise } from '@utils/cordovaChecker'
import { HeartbeatUtil } from '@utils/HeartbeatUtil'
import { AuthProvider, useAuth } from '@services/auth/AuthContext'
import { initCurrentUser, resetCurrentUser, hasNoAssignedPermissions, isCurrentUserLoaded, onCurrentUserLoaded, onCurrentUserChanged, revalidateCurrentUser } from '@api/userAndStoreContext'
import { registerAuthDeniedHandler } from '@coral/CoralRequestExecutor'
import PermissionsRequiredPage from '@pages/PermissionsRequiredPage'
import AccountSetupPage from '@pages/AccountSetupPage'
import { useAccountSetupGate } from '@pages/AccountSetupPage/useAccountSetupGate'
import { useLocalizationSync } from '@hooks/useLocalizationSync'

import { FeatureFlagProvider } from '@services/featureFlags'

const app = navigator.userAgent.match(/ATv3/)
if (app) {
    console.log('JWOStoreManagementPlatform: Loading cordova script')
    import('../utils/cordova')
}

/**
 * Redirects /web to / while preserving all query parameters.
 * Requirements: 3.1–3.2
 */
const WebRedirect = () => {
    const [searchParams] = useSearchParams()
    const queryString = searchParams.toString()
    const target = queryString ? `/?${queryString}` : '/'
    return <Navigate to={target} replace={true} />
}

const AppRoutes = () => {
    useBackButtonNavigation()
    const location = useLocation()

    const latencyTrackerRef = useRef<PageLatencyTracker | null>(null)
    const routeChangeCallbackRef = useRef<(() => void) | null>(null)

    if (latencyTrackerRef.current === null) {
        latencyTrackerRef.current = new PageLatencyTracker('ATBaseHome', (callback) => {
            routeChangeCallbackRef.current = callback
            return () => {
                routeChangeCallbackRef.current = null
            }
        })
    }

    useEffect(() => {
        if (routeChangeCallbackRef.current) {
            routeChangeCallbackRef.current()
        }
    }, [location])

    return (
        <Routes>
            <Route path="/provision" element={<ProvisionPage />} />
            <Route path="/logout" element={<LogoutPage />} />
            <Route path="/store-id-selection" element={<StoreIdSelectionPage />} />
            <Route path="/user-type-selection" element={<UserTypeSelectionPage />} />
            <Route path="/store-selection" element={<StoreIdSelectionPageFor3P />} />
            <Route path="/input-store-id" element={<StoreIdInputPage />} />
            <Route path="/home" element={<ToolLayout latencyTracker={latencyTrackerRef.current} />} />
            <Route path="/web" element={<WebRedirect />} />
            {/* https://app.asana.com/1/8442528107068/project/1213997537694234/task/1215183552021116?focus=true */}
            <Route path="/" element={<PlatformModeSelector latencyTracker={latencyTrackerRef.current} />} />
        </Routes>
    )
}

const AppContent = (): React.ReactElement => {
    const effectiveStoreId = useSelector(getEffectiveStoreId)
    const selectedStoreId = useSelector(getSelectedStoreId)
    // Use selectedStore (picker choice) over effectiveStore, skip NO_CURRENT_STORE
    const ffStoreId = (selectedStoreId && selectedStoreId !== NO_CURRENT_STORE)
        ? selectedStoreId
        : (effectiveStoreId && effectiveStoreId !== NO_CURRENT_STORE)
            ? effectiveStoreId
            : null
    const [isOnline, setIsOnline] = useState(true)
    const [isAppInitializing, setIsAppInitializing] = useState(true)

    const { isAuthenticated, isAuthLoading } = useAuth()

    // Account-setup gate (shared-pool migration): on unless the loaded feature config defines the
    // flag off. Defaulting on is safe because the gate releases at once for anyone not
    // mid-migration (see AccountSetupController). Evaluated only once auth settles and feature
    // config has loaded, so the flag read is meaningful.
    const accountSetupGateEnabled = !isAppInitializing &&
        FeatureConfigService.isFeatureEnabled(FeatureConfigService.featureConfigFeatures.ENABLE_ACCOUNT_SETUP_GATE, true)
    const accountSetup = useAccountSetupGate(isAuthenticated && !isAuthLoading && accountSetupGateEnabled)

    // Applies the selected locale to the message bundles on every route, not just
    // the Provision page — the native shells deep-link straight to #/home with a
    // ?locale= param and never pass through provisioning.
    useLocalizationSync()

    // Track current-user (permissions) load so renderContent re-evaluates the
    // no-permissions gate once the fetch resolves.
    const [isUserLoaded, setIsUserLoaded] = useState(isCurrentUserLoaded())
    // Counter that increments on every current-user state change so
    // renderContent re-evaluates hasNoAssignedPermissions() after mid-session
    // revalidations (P465134335). isUserLoaded flips true once and stays;
    // subsequent revalidations only update via this counter.
    const [, setUserStateVersion] = useState(0)
    useEffect(() => {
        if (!isUserLoaded) {
            onCurrentUserLoaded(() => setIsUserLoaded(true))
        }
    }, [isUserLoaded])

    // Mid-session access-revocation detection (P465134335).
    // When a Field Technician's expiry passes or an admin disables the user
    // while they're still signed in, the platform's cached getCurrentUser
    // state is stale — the user keeps seeing tools that all 403 on API calls.
    // Two triggers refresh that state:
    //   1. Tab regains focus (visibilitychange) — catches the "user was in a
    //      tool tab when their access expired, then came back to platform".
    //   2. Any 403 from a platform-side Coral call — catches the "user is
    //      actively on the platform tab and clicks something that fails".
    // After the fresh fetch resolves, if the user now has no permissions,
    // renderContent's existing gate shows PermissionsRequiredPage.
    useEffect(() => {
        if (!isAuthenticated) return

        const unsubscribe = onCurrentUserChanged(() => {
            // Force re-render so hasNoAssignedPermissions() is re-evaluated.
            setUserStateVersion((v) => v + 1)
        })

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void revalidateCurrentUser()
            }
        }
        document.addEventListener('visibilitychange', onVisibilityChange)

        // 403 on a platform-side call is a strong signal that the session was
        // revoked; force=true bypasses the cooldown for immediate re-check.
        registerAuthDeniedHandler(() => {
            void revalidateCurrentUser({ force: true })
        })

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange)
            registerAuthDeniedHandler(null)
            unsubscribe()
        }
    }, [isAuthenticated])

    useEffect(() => {
        console.log('[App] Initializing...')
        initializeAppUtils()

        // Initialize network monitoring
        const networkMonitoring = initNetworkMonitoring(setIsOnline)

        const featureConfigPromise =
            getCordovaReadyPromise()
                .catch(() => {}) // to prevent extra error message
                .finally(() => FeatureConfigService.load())

        featureConfigPromise
            .then(() => {
                HeartbeatUtil.init()
                ZoneService.initLocalization((label) => 'Unknown')
                if (StoreService.isNotifiable()) {
                    console.log('JWOStoreManagementPlatform: Initializing notifications capability')
                    HeliosInterruptService.init(EnvironmentDetail.getStoreId())
                }
                AcknowledgeNotificationService.init(EnvironmentDetail.getStoreId())
                PushNotificationService.activate()
                setIsAppInitializing(false)
            })
            .catch(() => {}) // to prevent extra error message

        // Cleanup network monitoring on unmount
        return networkMonitoring
    }, [])

    useEffect(() => {
        if (isAuthenticated && !isAuthLoading) {
            initCurrentUser()
        } else if (!isAuthenticated && !isAuthLoading) {
            resetCurrentUser()
        }
    }, [isAuthenticated, isAuthLoading])

    // Signal to RN app that platform is ready (used for login-to-SMP-load latency)
    const platformReadySent = useRef(false)
    const telemetryRef = useRef<Telemetry | null>(null)
    if (!telemetryRef.current) {
        telemetryRef.current = new Telemetry('ihm-associate-tools-base', {
            ...DefaultTelemetryConfig(),
            client: null,
        })
    }
    useEffect(() => {
        if (!isAuthenticated) {
            platformReadySent.current = false
            return
        }
        if (!isAppInitializing && !isAuthLoading && !platformReadySent.current && isReactNativeApp() && (window as any).ReactNativeWebView) {
            platformReadySent.current = true
            const rn = (window as any).ReactNativeWebView
            rn.postMessage(JSON.stringify({
                type: 'platform_ready',
                timestamp: new Date().toISOString(),
            }))
        }
    }, [isAppInitializing, isAuthenticated, isAuthLoading])

    const renderContent = () => {
        if (isAppInitializing || isAuthLoading) {
            return <div/>
        }

        if (isReactNativeApp() && !isAuthenticated) {
            console.log('[App] Not authenticated, waiting for native login')
            return <div/>
        }

        if (!isReactNativeApp() && !isAuthenticated) {
            return (
                <React.Suspense fallback={<div/>}>
                    <WebLoginPage onLoginSuccess={() => window.location.reload()} />
                </React.Suspense>
            )
        }

        // Account-setup gate: a freshly migrated user has no roles until the backend re-keys
        // them, so hold here — above the permissions gate, which would misread that transient
        // state as "no access". The network banner renders with the gate so an offline user
        // waiting on the loader can see why nothing progresses. A slow 'checking' (in-app
        // silent probe) surfaces a neutral loader after 500 ms rather than holding a blank
        // screen. The probe is network-bound (token bridge, discovery, userInfo) and in QA
        // outlasted 500 ms on every in-app login, so the account-setup copy must wait for its
        // PENDING verdict or every in-app user would read it as their account being moved.
        if (accountSetupGateEnabled && accountSetup.phase === 'checking') {
            return (
                <>
                    <NetworkBanner isOnline={isOnline} />
                    <AccountSetupPage phase="checking" onKeepWaiting={accountSetup.keepWaiting} appearAfterMs={500} />
                </>
            )
        }
        if (accountSetupGateEnabled && accountSetup.phase !== 'ready' && accountSetup.phase !== 'checking') {
            return (
                <>
                    <NetworkBanner isOnline={isOnline} />
                    <AccountSetupPage phase={accountSetup.phase} onKeepWaiting={accountSetup.keepWaiting} />
                </>
            )
        }

        // Authenticated, but the current-user/permissions fetch hasn't resolved
        // yet. Wait on a blank state rather than falling through to the main app
        // (which would briefly render the store-selection sheet before we know
        // the user has no permissions — the "store selector flash" on reload).
        // isUserLoaded flips once initCurrentUser() resolves OR fails (it is set
        // in the fetch's finally block), so this never stalls.
        if (!isUserLoaded) {
            return <div/>
        }

        // Authenticated but with no assigned permissions (no allowed actions and
        // not an admin): show a clear, actionable message instead of an empty app.
        // Rendered here — above AppRoutes, the store-selection sheet, and the tool
        // iframes — so nothing overlays it. Applies to both web and app.
        if (isUserLoaded && hasNoAssignedPermissions()) {
            return <PermissionsRequiredPage />
        }

        console.log('[App] Authenticated, showing main app')

        return (
            <>
                <FeatureFlagProvider storeId={ffStoreId}>
                    <meta name="viewport" content="height=device-height, width=device-width, initial-scale=1.0,
                        minimum-scale=1.0, maximum-scale=1.0, user-scalable=no, target-densitydpi=device-dpi"/>

                    {/* Display network status banner when offline */}
                    <NetworkBanner isOnline={isOnline} />

                    <AppRoutes />

                    {/* Store Selection Sheet - appears as overlay after login if no store selected */}
                    <StoreSelectionSheetController />

                    <IFrameController/>
                    <SeawardIFrameController/>
                </FeatureFlagProvider>
            </>
        )
    }

    return (
        <>
            {/* Route platform logs + metrics to the environment-appropriate pipeline:
                CloudWatch RUM on web (WebLog/WebMetric interceptors), React Native
                WebView on native. Rendered unconditionally so telemetry is captured
                during all app states. */}
            <MonitoringInterceptor toolId="SMP-platform" />
            {renderContent()}
        </>
    )
}

const initializeAppUtils = () => {
    console.log('JWOStoreManagementPlatform: Initializing App...')
    CrossFrameUtils.init()
    registerCrossFrameListeners()
    MetricsRecorder.registerListeners()
    MetricsBatcher.registerInterval()
    ActivityHeartbeatService.init()
    ApplicationTimeline.setTimelineId('ATBaseTool')
}

const App = (): React.ReactElement => {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    )
}

export default App
